import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { supabase } from '../services/supabase'
import Card from './Card'
import { Calendar, Activity, FileText, Heart, Clock } from 'lucide-react'
import { formatDate } from '../utils/helpers'

const HistoryTimeline = ({ clientId, organizationId }) => {
  const [timelineItems, setTimelineItems] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (clientId && organizationId) {
      fetchTimeline()
    }
  }, [clientId, organizationId])

  const fetchTimeline = async () => {
    if (!clientId || !organizationId) return
    try {
      setLoading(true)
      const [
        { data: examinations },
        { data: carePlans },
        { data: sessions },
        { data: therapies },
        { data: historyVersions },
      ] = await Promise.all([
        supabase
          .from('examinations')
          .select('id, created_at, vital_signs')
          .eq('client_id', clientId)
          .eq('organization_id', organizationId)
          .order('created_at', { ascending: false }),
        supabase
          .from('care_plans')
          .select('id, title, created_at, status')
          .eq('client_id', clientId)
          .eq('organization_id', organizationId)
          .order('created_at', { ascending: false }),
        supabase
          .from('sessions')
          .select('id, session_date, focus_area, outcome_summary')
          .eq('client_id', clientId)
          .eq('organization_id', organizationId)
          .order('session_date', { ascending: false }),
        supabase
          .from('therapies')
          .select('id, name, scheduled_date, status')
          .eq('client_id', clientId)
          .eq('organization_id', organizationId)
          .order('scheduled_date', { ascending: false }),
        supabase
          .from('medical_history_versions')
          .select('id, created_at, version_number, changed_fields')
          .eq('client_id', clientId)
          .eq('organization_id', organizationId)
          .order('created_at', { ascending: false }),
      ])

      const items = []

      // Add examinations
      examinations?.forEach((exam) => {
        items.push({
          id: `exam-${exam.id}`,
          type: 'examination',
          date: exam.created_at,
          title: 'Examination',
          description: exam.vital_signs?.bloodPressure
            ? `BP: ${exam.vital_signs.bloodPressure}, HR: ${exam.vital_signs.heartRate}`
            : 'Vital signs recorded',
          icon: Activity,
          color: 'primary',
        })
      })

      // Add care plans
      carePlans?.forEach((plan) => {
        items.push({
          id: `plan-${plan.id}`,
          type: 'care_plan',
          date: plan.created_at,
          title: plan.title,
          description: `Status: ${plan.status}`,
          icon: Heart,
          color: 'secondary',
        })
      })

      // Add sessions
      sessions?.forEach((session) => {
        items.push({
          id: `session-${session.id}`,
          type: 'session',
          date: session.session_date,
          title: session.focus_area || 'Session',
          description: session.outcome_summary?.substring(0, 100) || 'Session recorded',
          icon: FileText,
          color: 'accent',
        })
      })

      // Add therapies
      therapies?.forEach((therapy) => {
        items.push({
          id: `therapy-${therapy.id}`,
          type: 'therapy',
          date: therapy.scheduled_date,
          title: therapy.name,
          description: `Status: ${therapy.status}`,
          icon: Calendar,
          color: 'primary',
        })
      })

      // Add history versions
      historyVersions?.forEach((version) => {
        const changedCount = Object.keys(version.changed_fields || {}).length
        items.push({
          id: `version-${version.id}`,
          type: 'history_version',
          date: version.created_at,
          title: `History Updated (v${version.version_number})`,
          description: `${changedCount} field(s) changed`,
          icon: Clock,
          color: 'gray',
        })
      })

      // Sort by date
      items.sort((a, b) => new Date(b.date) - new Date(a.date))
      setTimelineItems(items)
    } catch (error) {
      console.error('Error fetching timeline:', error)
    } finally {
      setLoading(false)
    }
  }

  const getColorClasses = (color) => {
    const colors = {
      primary: 'bg-primary-100 text-primary-600 border-primary-200',
      secondary: 'bg-secondary-100 text-secondary-600 border-secondary-200',
      accent: 'bg-accent-100 text-accent-600 border-accent-200',
      gray: 'bg-gray-100 text-gray-600 border-gray-200',
    }
    return colors[color] || colors.gray
  }

  if (loading) {
    return <div className="text-sm text-gray-500">Loading timeline...</div>
  }

  if (timelineItems.length === 0) {
    return (
      <Card variant="soft" className="p-6 text-center">
        <Activity size={32} className="mx-auto mb-2 text-gray-400" />
        <p className="text-sm text-gray-500">No timeline data available</p>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Clock size={20} />
        <h3 className="text-lg font-semibold text-gray-900">History Timeline</h3>
      </div>

      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary-200 via-secondary-200 to-accent-200"></div>

        {/* Timeline Items */}
        <div className="space-y-6">
          {timelineItems.map((item, index) => {
            const Icon = item.icon
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="relative flex items-start gap-4"
              >
                {/* Timeline Dot */}
                <div className={`relative z-10 w-12 h-12 rounded-full ${getColorClasses(item.color)} flex items-center justify-center border-2 shadow-md`}>
                  <Icon size={20} />
                </div>

                {/* Content */}
                <Card variant="soft" className="flex-1 p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-gray-900">{item.title}</h4>
                      <p className="text-xs text-gray-500 mt-1">{formatDate(item.date)}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${getColorClasses(item.color)}`}>
                      {item.type.replace('_', ' ')}
                    </span>
                  </div>
                  {item.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">{item.description}</p>
                  )}
                </Card>
              </motion.div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default HistoryTimeline

